import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { Back, Field, Primary, Sheet, inputCls, money } from "@/components/backoffice/kit";
import { Toggle } from "@/components/account/Shell";
import { FulfillmentSwitch, PRINTS_OFF, PRINTS_ON } from "@/components/account/FulfillmentSwitch";

/** 8.5 product edit — one screen, the six generic fields, the price block swaps with the 13thav prints switch, Save. */
export default function ProductEdit({ product, consignment, isHome, back, onSaved }) {
    const p = product;
    const [f, setF] = useState({});
    const [busy, setBusy] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const [askUnlock, setAskUnlock] = useState(false);
    const file = useRef(null);
    useEffect(() => {
        setF({
            name: p.name || "", photo: p.image || "", photoChanged: false,
            markup: p.markup_pct ?? p.effective_markup_pct ?? consignment?.default_markup ?? 0,
            price: p.from_price ?? 0, consign: p.fulfillment === "consignment", visible: p.simple_visible !== false,
            sizes: (p.size_presets || []).join(", "), qtys: (p.qty_presets || []).join(", "),
            designer: !!p.designer_enabled, note: p.note_example || "",
        });
    }, [p, consignment]);
    const standard = p.standard_price ?? p.from_price ?? 0;
    const rate = consignment?.rate ?? 0.2;
    const maxMarkup = consignment?.max_markup ?? 100;
    const markup = Math.max(0, Math.min(Number(f.markup || 0), maxMarkup));
    const customer = Math.round(standard * (1 + markup / 100) * 100) / 100;
    const keep = Math.round((standard * rate + standard * markup / 100) * 100) / 100;
    const upload = async (file0) => {
        if (!file0) return;
        setBusy(true);
        try { const rec = await uploadArtwork(file0); setF((s) => ({ ...s, photo: rec.url, photoChanged: true })); } catch { toast.error("Upload failed. Try again."); } finally { setBusy(false); }
    };
    const save = async () => {
        setBusy(true);
        try {
            const body = {
                simple_visible: f.visible, note_example: f.note, designer_enabled: f.designer,
                size_presets: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
                qty_presets: f.qtys.split(",").map((s) => Number(s.trim())).filter((n) => n > 0),
            };
            if (p.kind === "shirt") delete body.size_presets;
            if (f.photoChanged) body.photo = f.photo;
            if (f.name.trim() && f.name.trim() !== p.name) body[p.inherited ? "title_override" : "name"] = f.name.trim();
            if (!isHome) {
                body.fulfillment = f.consign ? "consignment" : "self";
                if (f.consign) body.markup_pct = markup;
                else body.your_price = Math.max(Number(f.price || 0), 0.01);
            } else if (!p.price_locked || unlocked) {
                body.your_price = Math.max(Number(f.price || 0), 0.01);
            }
            const { data } = await api.patch(`/store/catalog/${p.kind}/${p.id}`, body);
            toast.success("Saved");
            onSaved(data.product);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    if (f.name === undefined) return null;
    const consignOn = !isHome && f.consign;
    return (
        <div className="grid gap-4" data-testid="product-edit">
            <Back to={back} testid="product-back" />
            <h1 className="text-[22px] font-semibold">{p.name}</h1>
            <Field label="Photo">
                <div className="flex items-center gap-3">
                    <div className="h-24 w-24 border border-[#1a1a1a] bg-white">{f.photo && <img src={f.photo} alt="" className="h-full w-full object-contain" />}</div>
                    <button type="button" onClick={() => file.current?.click()} disabled={busy} data-testid="product-change-photo"
                        className="inline-flex min-h-[44px] items-center gap-2 border border-[#1a1a1a] bg-white px-3 text-[15px] font-medium"><Upload size={16} /> Change photo</button>
                    <input ref={file} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                </div>
            </Field>
            <Field label="Name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-name" /></Field>

            <section className="border border-[#1a1a1a] p-3" data-testid="price-block">
                {!isHome && <FulfillmentSwitch on={f.consign} onChange={(v) => setF({ ...f, consign: v })} testid="product-fulfillment" text={false} />}
                {consignOn ? (
                    <div className="mt-2 grid gap-2" data-testid="price-markup">
                        <Field label="Markup %" hint={`0–${maxMarkup}. Added on top of 13thav's standard price.`}>
                            <input type="number" inputMode="decimal" min="0" max={maxMarkup} step="0.5" className={inputCls} value={f.markup} onChange={(e) => setF({ ...f, markup: e.target.value })} data-testid="product-markup" />
                        </Field>
                        <p className="text-[15px]"><span className="text-[#767676]">Standard {money(standard)}</span> · Customer pays <b data-testid="price-customer">{money(customer)}</b> · You keep <b data-testid="price-keep">{money(keep)}</b></p>
                        <p className="text-[14px] text-[#4d4d4d]">13thav prints this: add your markup on top of 13thav's price. {PRINTS_ON}</p>
                    </div>
                ) : (
                    <div className="mt-2 grid gap-2" data-testid="price-your">
                        <Field label="Your price" hint={isHome ? (p.price_locked ? `Locked — ${p.price_source || "QuickBooks"}` : p.print_group ? `Live Next Day Flyers price for the default selection${p.ndf_pulled ? ` · pulled ${p.ndf_pulled}` : ""}. Set the platform markup under Payouts & fees.` : "The standard price every shop inherits.") : "Any price, $0.01 minimum. You keep the full sale."}>
                            <input type="number" inputMode="decimal" min="0.01" step="0.01" className={`${inputCls} disabled:bg-[#f2f2f2] disabled:text-[#767676]`} value={f.price} disabled={isHome && ((p.price_locked && !unlocked) || !!p.print_group)}
                                onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="product-price" />
                        </Field>
                        {isHome && p.price_locked && !unlocked && (
                            <button type="button" onClick={() => setAskUnlock(true)} className="min-h-[44px] justify-self-start underline" data-testid="price-unlock">Change the locked price</button>
                        )}
                        {isHome && p.qb_reference && (
                            <p className="text-[14px] text-[#4d4d4d]" data-testid="qb-reference"><span className="font-semibold">QuickBooks reference:</span> {p.qb_reference}</p>
                        )}
                        {!isHome && (
                            <p className="text-[15px]">
                                <span className="text-[#767676]">Standard {money(standard)}</span>
                                {" · "}<button type="button" onClick={() => setF({ ...f, price: standard })} className="min-h-[44px] underline" data-testid="price-reset">Reset to standard price</button>
                            </p>
                        )}
                        {!isHome && <p className="text-[14px] text-[#4d4d4d]">You print this: set any price; you keep the full sale. {PRINTS_OFF}</p>}
                    </div>
                )}
            </section>

            {p.kind !== "shirt" && <Field label="Sizes" hint="Comma-separated — 8.5×11, 11×17"><input className={inputCls} value={f.sizes} onChange={(e) => setF({ ...f, sizes: e.target.value })} data-testid="product-sizes" /></Field>}
            <Field label="Quantity buttons" hint="Comma-separated — 25, 50, 100"><input className={inputCls} value={f.qtys} onChange={(e) => setF({ ...f, qtys: e.target.value })} data-testid="product-qtys" /></Field>
            <Field label="Designer"><Toggle on={f.designer} onChange={(v) => setF({ ...f, designer: v })} testid="product-designer" labelOn="On" labelOff="Off — upload only" /></Field>
            <Field label="Note example"><input className={inputCls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Example: logo left chest, names on back" data-testid="product-note" /></Field>
            <Field label={isHome ? "Show in catalog" : "Show on my shop"}><Toggle on={f.visible} onChange={(v) => setF({ ...f, visible: v })} testid="product-visible" labelOn="Shown" labelOff="Hidden" /></Field>
            <Primary onClick={save} disabled={busy} testid="product-save" full>Save</Primary>
            {askUnlock && (
                <Sheet title="Change a locked price?" onClose={() => setAskUnlock(false)} testid="unlock-sheet">
                    <p className="text-[16px]">This price comes from QuickBooks ({p.price_source || "locked rate card"}). Change it?</p>
                    <div className="mt-3"><Primary onClick={() => { setUnlocked(true); setAskUnlock(false); }} testid="unlock-confirm" full>Yes, change it</Primary></div>
                </Sheet>
            )}
        </div>
    );
}
